/**
 * PaymentMethodSelector — renders available payment options for an invoice
 * and orchestrates the full initiation flow.
 *
 * Supported providers:
 *   denizbank      → hidden form POST (new tab or same window)
 *   netahsilat     → hidden form POST (new tab or same window)
 *   merchantsafepay → redirect to external payment page
 *   bank_transfer  → receipt upload drawer
 *
 * The parent passes `availableProviders` from the institution's payment
 * settings; this component drives state from idle → initiated.
 */
import { useState, useEffect, useRef } from 'react'
import { CreditCard, Building2, ChevronRight, Loader2, AlertCircle } from 'lucide-react'
import { initiatePayment } from '@/services/paymentService'
import type { PaymentInitiateRequest, FormPostResponse, PaymentProvider } from '@/services/paymentService'
import { DenizbankPaymentButton, NetahsilatPaymentButton } from './FormPostButton'
import { MerchantSafePayButton } from './MerchantSafePayButton'
import { BankTransferUpload } from './BankTransferUpload'

export type { PaymentProvider }

interface BillingInfo {
  name?: string
  phone?: string
  address?: string
  city?: string
  district?: string
}

interface PaymentMethodSelectorProps {
  invoiceId: number
  currencyCode: string
  availableProviders: PaymentProvider[]
  billing?: BillingInfo
  /** HTML string with bank account details configured in portal config. */
  bankTransferInfo?: string | null
  onSuccess?: () => void
}

const PROVIDER_LABELS: Record<PaymentProvider, string> = {
  denizbank: 'Denizbank',
  netahsilat: 'Netahsilat',
  merchantsafepay: 'MerchantSafePay',
  bank_transfer: 'Bank Transfer',
}

const PROVIDER_ICONS: Record<PaymentProvider, React.ReactNode> = {
  denizbank: <CreditCard className="h-5 w-5" />,
  netahsilat: <CreditCard className="h-5 w-5" />,
  merchantsafepay: <CreditCard className="h-5 w-5" />,
  bank_transfer: <Building2 className="h-5 w-5" />,
}

type FlowState =
  | { stage: 'selecting' }
  | { stage: 'loading'; provider: PaymentProvider }
  | { stage: 'form_post'; provider: PaymentProvider; response: FormPostResponse }
  | { stage: 'merchantsafepay'; provider: 'merchantsafepay' }
  | { stage: 'bank_transfer' }
  | { stage: 'error'; message: string }

export function PaymentMethodSelector({
  invoiceId,
  currencyCode,
  availableProviders,
  billing,
  bankTransferInfo,
  onSuccess,
}: PaymentMethodSelectorProps) {
  const [flowState, setFlowState] = useState<FlowState>({ stage: 'selecting' })
  const autoInitiated = useRef(false)

  useEffect(() => {
    if (!autoInitiated.current && availableProviders.length === 1) {
      autoInitiated.current = true
      void handleSelectProvider(availableProviders[0])
    }
  // availableProviders is a stable prop set before mount; autoInitiated guards against re-firing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProviders])

  async function handleSelectProvider(provider: PaymentProvider) {
    if (provider === 'merchantsafepay') {
      setFlowState({ stage: 'merchantsafepay', provider })
      return
    }

    // All providers (including bank_transfer) go through /initiate/ to create a Payment row.
    setFlowState({ stage: 'loading', provider })

    const payload: PaymentInitiateRequest = {
      provider,
      installment_count: 1,
      currency_code: currencyCode,
      billing,
    }

    try {
      const result = await initiatePayment(invoiceId, payload)

      if (result.type === 'bank_transfer') {
        setFlowState({ stage: 'bank_transfer' })
        return
      }

      if (result.type !== 'form_post') {
        throw new Error('Unexpected response type.')
      }
      setFlowState({ stage: 'form_post', provider, response: result })
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Could not initiate payment. Please try again.'
      setFlowState({ stage: 'error', message })
    }
  }

  // ------------------------------------------------------------------
  // Render helpers
  // ------------------------------------------------------------------

  if (flowState.stage === 'loading') {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-600">
          Preparing {PROVIDER_LABELS[flowState.provider]} payment…
        </p>
      </div>
    )
  }

  if (flowState.stage === 'form_post') {
    const { provider, response } = flowState
    const ButtonComponent =
      provider === 'denizbank' ? DenizbankPaymentButton : NetahsilatPaymentButton

    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <p className="text-sm text-gray-600">
          Click the button below to proceed to the {PROVIDER_LABELS[provider]} payment page.
        </p>
        <ButtonComponent
          actionUrl={response.action_url}
          fields={response.fields}
          openInSameWindow={response.open_in_same_window}
        />
        <button
          type="button"
          onClick={() => setFlowState({ stage: 'selecting' })}
          className="text-sm text-gray-500 underline"
        >
          Choose a different payment method
        </button>
      </div>
    )
  }

  if (flowState.stage === 'merchantsafepay') {
    return (
      <div className="py-4">
        <MerchantSafePayButton invoiceId={invoiceId} />
        <button
          type="button"
          onClick={() => setFlowState({ stage: 'selecting' })}
          className="mt-4 block w-full text-center text-sm text-gray-500 underline"
        >
          Choose a different payment method
        </button>
      </div>
    )
  }

  if (flowState.stage === 'bank_transfer') {
    return (
      <div className="py-4">
        <p className="mb-4 text-sm text-gray-600">
          Upload a receipt (PDF, JPEG, or PNG) after completing your bank transfer.
        </p>
        <BankTransferUpload
          invoiceId={invoiceId}
          bankTransferInfo={bankTransferInfo}
          onSuccess={() => onSuccess?.()}
        />
      </div>
    )
  }

  if (flowState.stage === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <p className="text-sm font-medium">{flowState.message}</p>
        </div>
        <button
          type="button"
          onClick={() => setFlowState({ stage: 'selecting' })}
          className="text-sm text-gray-500 underline"
        >
          Try again
        </button>
      </div>
    )
  }

  // Default: selecting
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {availableProviders.map((provider) => (
          <li key={provider}>
            <button
              type="button"
              onClick={() => handleSelectProvider(provider)}
              className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-4 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 active:bg-blue-100 min-h-[56px]"
            >
              <span className="flex items-center gap-3">
                <span className="text-blue-600">{PROVIDER_ICONS[provider]}</span>
                <span className="text-sm font-medium text-gray-800">
                  {PROVIDER_LABELS[provider]}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
